import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Coins, 
  BarChart3, PieChart, Activity, ArrowUp, ArrowDown,
  Wallet, Lock, Droplets, Clock, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const [tokenInfo, setTokenInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [timeframe, setTimeframe] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [tokenRes, statsRes] = await Promise.all([
        axios.get(`${API}/token/info`),
        axios.get(`${API}/stats/ecosystem`)
      ]);
      setTokenInfo(tokenRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
    }
    setIsLoading(false);
  };

  // Mock data for charts (in production, this would come from the API)
  const kpiData = {
    treasury: {
      total: 2500000,
      btcHoldings: 42.5,
      ethHoldings: 850,
      change24h: 3.2,
    },
    token: {
      price: 0.68,
      marketCap: 68000000,
      volume24h: 1250000,
      change24h: 5.8,
      holders: 2847,
    },
    staking: {
      totalStaked: 15750000,
      stakingRate: 23.5,
      avgApy: 14.2,
      change24h: 1.5,
    },
    liquidity: {
      tvl: 4250000,
      volume24h: 850000,
      fees24h: 2550,
      change24h: -2.1,
    },
  };

  const priceHistory = [
    { date: 'Mon', price: 0.62 },
    { date: 'Tue', price: 0.64 },
    { date: 'Wed', price: 0.63 },
    { date: 'Thu', price: 0.67 },
    { date: 'Fri', price: 0.65 },
    { date: 'Sat', price: 0.68 },
    { date: 'Sun', price: 0.68 },
  ];

  const maxPrice = Math.max(...priceHistory.map(p => p.price));
  const minPrice = Math.min(...priceHistory.map(p => p.price));

  const StatCard = ({ title, value, change, icon: Icon, prefix = '', suffix = '', color = 'text-[#D4AF37]' }) => (
    <div className="glass-panel p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-white/60 text-sm">{title}</span>
        <Icon className={color} size={18} />
      </div>
      <p className="font-unbounded text-2xl font-bold text-white mb-1">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          <span>{Math.abs(change)}% (24h)</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-unbounded text-4xl font-bold text-white mb-2">
              Platform <span className="gradient-text">Analytics</span>
            </h1>
            <p className="text-white/70">Real-time metrics and insights for the AIH ecosystem</p>
          </div>
          <Button 
            onClick={fetchData} 
            variant="outline" 
            className="btn-secondary flex items-center gap-2"
            disabled={isLoading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>

        {/* Timeframe Selector */}
        <div className="flex gap-2 mb-8">
          {['24h', '7d', '30d', '90d', '1y'].map(tf => (
            <Button
              key={tf}
              variant={timeframe === tf ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeframe(tf)}
              className={timeframe === tf ? 'btn-primary' : 'btn-secondary'}
            >
              {tf}
            </Button>
          ))}
        </div>

        {/* Main KPI Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Token Price" 
            value={kpiData.token.price} 
            prefix="$"
            change={kpiData.token.change24h}
            icon={Coins}
          />
          <StatCard 
            title="Market Cap" 
            value={`${(kpiData.token.marketCap / 1000000).toFixed(1)}M`}
            prefix="$"
            icon={DollarSign}
            color="text-green-400"
          />
          <StatCard 
            title="Total Staked" 
            value={`${(kpiData.staking.totalStaked / 1000000).toFixed(1)}M`}
            suffix=" AIH"
            change={kpiData.staking.change24h}
            icon={Lock}
            color="text-purple-400"
          />
          <StatCard 
            title="TVL (Liquidity)" 
            value={`${(kpiData.liquidity.tvl / 1000000).toFixed(2)}M`}
            prefix="$"
            change={kpiData.liquidity.change24h}
            icon={Droplets}
            color="text-blue-400"
          />
        </div>

        {/* Analytics Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-[#0A1628] p-1">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="treasury" data-testid="tab-treasury">Treasury</TabsTrigger>
            <TabsTrigger value="staking" data-testid="tab-staking">Staking</TabsTrigger>
            <TabsTrigger value="liquidity" data-testid="tab-liquidity">Liquidity</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Price Chart */}
              <div className="lg:col-span-2 glass-panel p-6" data-testid="price-chart">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-unbounded text-xl font-bold text-white">Price History</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-[#D4AF37]">${kpiData.token.price}</span>
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <ArrowUp size={14} /> {kpiData.token.change24h}%
                    </span>
                  </div>
                </div>
                
                {/* Simple Chart Visualization */}
                <div className="h-48 flex items-end justify-between gap-2">
                  {priceHistory.map((item, i) => {
                    const height = ((item.price - minPrice) / (maxPrice - minPrice)) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-2">
                        <div 
                          className="w-full bg-gradient-to-t from-[#D4AF37] to-[#D4AF37]/50 rounded-t-lg transition-all hover:from-[#D4AF37] hover:to-[#D4AF37]/70"
                          style={{ height: `${Math.max(height, 10)}%` }}
                        />
                        <span className="text-xs text-white/60">{item.date}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="glass-panel p-6" data-testid="quick-stats">
                <h3 className="font-unbounded text-xl font-bold text-white mb-6">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-white/10">
                    <span className="text-white/60">24h Volume</span>
                    <span className="text-white font-bold">${(kpiData.token.volume24h / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/10">
                    <span className="text-white/60">Holders</span>
                    <span className="text-white font-bold">{kpiData.token.holders.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/10">
                    <span className="text-white/60">Staking Rate</span>
                    <span className="text-purple-400 font-bold">{kpiData.staking.stakingRate}%</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/10">
                    <span className="text-white/60">Avg Staking APY</span>
                    <span className="text-green-400 font-bold">{kpiData.staking.avgApy}%</span>
                  </div>
                  <div className="flex justify-between py-3">
                    <span className="text-white/60">LP Fees (24h)</span>
                    <span className="text-blue-400 font-bold">${kpiData.liquidity.fees24h.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass-panel p-4 text-center">
                <Users className="mx-auto text-blue-400 mb-2" size={24} />
                <p className="text-2xl font-bold text-white">{stats?.total_users || '2,847'}</p>
                <p className="text-xs text-white/60">Total Users</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <Activity className="mx-auto text-green-400 mb-2" size={24} />
                <p className="text-2xl font-bold text-white">12,458</p>
                <p className="text-xs text-white/60">Transactions</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <BarChart3 className="mx-auto text-purple-400 mb-2" size={24} />
                <p className="text-2xl font-bold text-white">{stats?.total_courses || '6'}</p>
                <p className="text-xs text-white/60">Courses</p>
              </div>
              <div className="glass-panel p-4 text-center">
                <Clock className="mx-auto text-orange-400 mb-2" size={24} />
                <p className="text-2xl font-bold text-white">Aug 2026</p>
                <p className="text-xs text-white/60">Launch Date</p>
              </div>
            </div>
          </TabsContent>

          {/* Treasury Tab */}
          <TabsContent value="treasury" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard 
                title="Total Treasury" 
                value={`${(kpiData.treasury.total / 1000000).toFixed(2)}M`}
                prefix="$"
                change={kpiData.treasury.change24h}
                icon={Wallet}
                color="text-green-400"
              />
              <StatCard 
                title="BTC Holdings" 
                value={kpiData.treasury.btcHoldings}
                suffix=" BTC"
                icon={Coins}
                color="text-[#F7931A]"
              />
              <StatCard 
                title="ETH Holdings" 
                value={kpiData.treasury.ethHoldings}
                suffix=" ETH"
                icon={Coins}
                color="text-blue-400"
              />
            </div>

            <div className="glass-panel p-6">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Treasury Allocation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/80">Bitcoin (BTC)</span>
                      <span className="text-[#F7931A] font-bold">65%</span>
                    </div>
                    <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                      <div className="h-full bg-[#F7931A] rounded-full" style={{ width: '65%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/80">Ethereum (ETH)</span>
                      <span className="text-blue-400 font-bold">25%</span>
                    </div>
                    <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '25%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-white/80">Stablecoins</span>
                      <span className="text-green-400 font-bold">10%</span>
                    </div>
                    <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '10%' }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="relative w-48 h-48">
                    <div className="absolute inset-0 rounded-full border-[16px] border-[#F7931A]" style={{ clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%, 50% 100%)' }} />
                    <div className="absolute inset-0 rounded-full border-[16px] border-blue-500" style={{ clipPath: 'polygon(50% 50%, 50% 0%, 100% 0%)' }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">${(kpiData.treasury.total / 1000000).toFixed(1)}M</p>
                        <p className="text-xs text-white/60">Total Value</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Staking Tab */}
          <TabsContent value="staking" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard 
                title="Total Staked" 
                value={`${(kpiData.staking.totalStaked / 1000000).toFixed(1)}M`}
                suffix=" AIH"
                change={kpiData.staking.change24h}
                icon={Lock}
                color="text-purple-400"
              />
              <StatCard 
                title="Staking Rate" 
                value={kpiData.staking.stakingRate}
                suffix="%"
                icon={PieChart}
                color="text-blue-400"
              />
              <StatCard 
                title="Average APY" 
                value={kpiData.staking.avgApy}
                suffix="%"
                icon={TrendingUp}
                color="text-green-400"
              />
              <StatCard 
                title="Active Stakers" 
                value="1,847"
                icon={Users}
                color="text-orange-400"
              />
            </div>

            <div className="glass-panel p-6">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Staking Distribution by Tier</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="tier-badge platinum">Platinum</span>
                      <span className="text-white/60">25% APY</span>
                    </div>
                    <span className="text-white font-bold">45%</span>
                  </div>
                  <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-gray-300 to-white rounded-full" style={{ width: '45%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="tier-badge gold">Gold</span>
                      <span className="text-white/60">18% APY</span>
                    </div>
                    <span className="text-white font-bold">30%</span>
                  </div>
                  <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                    <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: '30%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="tier-badge silver">Silver</span>
                      <span className="text-white/60">12% APY</span>
                    </div>
                    <span className="text-white font-bold">15%</span>
                  </div>
                  <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                    <div className="h-full bg-gray-400 rounded-full" style={{ width: '15%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="tier-badge bronze">Bronze</span>
                      <span className="text-white/60">8% APY</span>
                    </div>
                    <span className="text-white font-bold">10%</span>
                  </div>
                  <div className="h-3 bg-[#0A1628] rounded-full overflow-hidden">
                    <div className="h-full bg-orange-700 rounded-full" style={{ width: '10%' }} />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Liquidity Tab */}
          <TabsContent value="liquidity" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard 
                title="Total Value Locked" 
                value={`${(kpiData.liquidity.tvl / 1000000).toFixed(2)}M`}
                prefix="$"
                change={kpiData.liquidity.change24h}
                icon={Droplets}
                color="text-blue-400"
              />
              <StatCard 
                title="24h Volume" 
                value={`${(kpiData.liquidity.volume24h / 1000).toFixed(0)}K`}
                prefix="$"
                icon={Activity}
                color="text-green-400"
              />
              <StatCard 
                title="24h Fees" 
                value={kpiData.liquidity.fees24h}
                prefix="$"
                icon={DollarSign}
                color="text-[#D4AF37]"
              />
              <StatCard 
                title="LP Providers" 
                value="342"
                icon={Users}
                color="text-purple-400"
              />
            </div>

            <div className="glass-panel p-6">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">AIH/ETH Pool Metrics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-2">AIH Reserve</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">52.5M</p>
                </div>
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-2">ETH Reserve</p>
                  <p className="text-2xl font-bold text-blue-400">1,850</p>
                </div>
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-2">Current Rate</p>
                  <p className="text-2xl font-bold text-white">1:28,378</p>
                </div>
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-2">Pool APR</p>
                  <p className="text-2xl font-bold text-green-400">42.5%</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Analytics;
